import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { api, getApiErrorCode } from '../utils/api'
import { clearStoredApiToken } from '../utils/apiAuth'
import { setActiveTenantKey } from '../offline/tenant'
import { useT } from '../i18n'

export default function ResetPassword() {
  const t = useT()
  const location = useLocation()
  const navigate = useNavigate()

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('token')?.trim() || ''
  }, [location.search])

  const [status, setStatus] = useState<'loading' | 'ready' | 'submitting' | 'success' | 'error'>('loading')
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [preview, setPreview] = useState<{
    purpose: 'activation' | 'passwordReset'
    displayName: string
    accountLabel: string
    email: string | null
    username: string | null
    expiresAt: string
  } | null>(null)

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setError(t('auth.resetTokenMissing'))
      return
    }

    let cancelled = false
    setStatus('loading')
    setError('')

    void api.auth.previewPasswordReset({ token })
      .then((result) => {
        if (cancelled) return
        setPreview({
          purpose: result.purpose,
          displayName: result.displayName,
          accountLabel: result.accountLabel,
          email: result.email,
          username: result.username,
          expiresAt: result.expiresAt
        })
        setEmail(result.email ?? '')
        setUsername(result.username ?? '')
        setStatus('ready')
      })
      .catch((cause) => {
        if (cancelled) return
        setStatus('error')
        setError(getApiErrorCode(cause) === 'memberRevoked' ? t('auth.memberRevoked') : cause instanceof Error ? cause.message : t('auth.resetInvalid'))
      })

    return () => {
      cancelled = true
    }
  }, [t, token])

  const submit = async () => {
    if (!token) {
      setStatus('error')
      setError(t('auth.resetTokenMissing'))
      return
    }

    if (password !== confirmPassword) {
      setStatus('error')
      setError(t('auth.resetPasswordMismatch'))
      return
    }

    setStatus('submitting')
    setError('')

    try {
      await api.auth.resetPassword({
        token,
        email,
        username,
        password
      })
      clearStoredApiToken()
      setActiveTenantKey(null)
      setStatus('success')
      navigate(`/sign-in?reset=success&identifier=${encodeURIComponent(email.trim() || username.trim())}`, { replace: true })
    } catch (cause) {
      setStatus('error')
      setError(getApiErrorCode(cause) === 'memberRevoked' ? t('auth.memberRevoked') : cause instanceof Error ? cause.message : t('auth.resetInvalid'))
    }
  }

  const title = preview?.purpose === 'activation'
    ? t('auth.resetActivationTitle')
    : t('auth.resetPasswordTitle')

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <p style={{ color: '#6b7280', marginTop: 8 }}>{t('auth.resetHelp')}</p>

      {status === 'loading' && (
        <div style={{ marginTop: 16, color: '#6b7280' }}>{t('auth.resetLoading')}</div>
      )}

      {status === 'error' && (
        <div style={{ marginTop: 16, padding: 12, borderRadius: 8, backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}>
          {error || t('auth.resetInvalid')}
        </div>
      )}

      {(status === 'ready' || status === 'submitting') && preview && (
        <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
          <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}>
            <div><strong>{preview.displayName}</strong></div>
            <div>{preview.accountLabel}</div>
            <div>{t('auth.resetExpiresAt')}: {new Date(preview.expiresAt).toLocaleString()}</div>
          </div>

          <label style={{ display: 'grid', gap: 8 }}>
            <span style={{ fontWeight: 600 }}>{t('auth.resetEmail')}</span>
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t('auth.resetEmailPlaceholder')}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db' }}
            />
          </label>

          <label style={{ display: 'grid', gap: 8 }}>
            <span style={{ fontWeight: 600 }}>{t('auth.resetUsername')}</span>
            <input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder={t('auth.resetUsernamePlaceholder')}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db' }}
            />
          </label>

          <label style={{ display: 'grid', gap: 8 }}>
            <span style={{ fontWeight: 600 }}>{t('auth.password')}</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t('auth.passwordPlaceholder')}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db' }}
            />
          </label>

          <label style={{ display: 'grid', gap: 8 }}>
            <span style={{ fontWeight: 600 }}>{t('auth.resetConfirmPassword')}</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder={t('auth.resetConfirmPasswordPlaceholder')}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db' }}
            />
          </label>

          <button
            type="button"
            onClick={() => void submit()}
            disabled={status === 'submitting'}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: '#111827',
              color: 'white',
              fontWeight: 800,
              cursor: status === 'submitting' ? 'not-allowed' : 'pointer'
            }}
          >
            {status === 'submitting'
              ? t('auth.resetSubmitting')
              : preview.purpose === 'activation'
                ? t('auth.resetActivateAction')
                : t('auth.resetPasswordAction')}
          </button>
        </div>
      )}
    </div>
  )
}